import {
  Box,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
  MenuItem,
  Select,
  Pagination,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { PieChart, Pie, Cell, Legend } from "recharts";
import { useEffect, useState } from "react";
import ZipCodeDetails from "../../Components/ZipTracker/ZipCodeDetails";
import {
  GET_ZIP_TRACKER,
  GET_ZIP_STATS,
  GET_ZIP_STATS_2,
} from "../../Utils/Queries";
import { useLazyQuery } from "@apollo/client";

const ZipTracker = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [timeframe, setTimeframe] = useState("This Month");
  const [waitlist, setWaitlist] = useState([]);
  const [pieChart, setPieChart] = useState([]);
  const [pieChart2, setPieChart2] = useState([]);
  const [selectedItem, setSelectedItem] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState({
    totalPages: 1,
    totalItems: 0,
    currentPage: 1,
    itemsPerPage: 10
  });

  const [getZipTracker, { data: zipData, loading: zipLoading }] = useLazyQuery(GET_ZIP_TRACKER);
  const [getZipStats, { data: zipStats }] = useLazyQuery(GET_ZIP_STATS);
  const [getZipStats2, { data: zipStats2 }] = useLazyQuery(GET_ZIP_STATS_2);

  const handleDrawerClose = () => setDrawerOpen(false);
  const handleDrawerOpen = (item) => {
    setDrawerOpen(true);
    setSelectedItem(item);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    getZipTracker({
      variables: {
        page: newPage,
        limit: itemsPerPage
      }
    });
  };

  // Initial data fetch
  useEffect(() => {
    getZipTracker({
      variables: {
        page: currentPage,
        limit: itemsPerPage
      }
    });
    getZipStats();
    getZipStats2();
  }, []);

  useEffect(() => {
    if (zipStats) {
      setPieChart(zipStats.getZipCodeStats);
    }
  }, [zipStats]);

  useEffect(() => {
    if (zipStats2) {
      setPieChart2(zipStats2.getActiveZipCodeStats);
    }
  }, [zipStats2]);

  useEffect(() => {
    if (zipData) {
      setWaitlist(zipData.fetchAllWaitlist?.items || []);
      
      // Update pagination info from the response
      if (zipData.fetchAllWaitlist?.pagination) {
        setPaginationInfo(zipData.fetchAllWaitlist.pagination);
      }
    }
  }, [zipData]);



  const renderDonut = (title, piechartData) => (
    <Box
      sx={{
        flex: 1,
        borderRadius: "4px",
        p: 2,
        minWidth: "300px",
        bgcolor: "#fff",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
          {title}
        </Typography>
        <Select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value)}
          size="small"
          sx={{ fontSize: "12px" }}
        >
          <MenuItem value="This Month">This Month</MenuItem>
          <MenuItem value="Last Month">Last Month</MenuItem>
        </Select>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "13px",
          color: "#333",
          mb: 0.5,
        }}
      >
        <PieChart width={200} height={200}>
          <Pie
            data={piechartData}
            dataKey="value"
            nameKey="name"
            outerRadius={70}
            innerRadius={40}
            paddingAngle={2}
          >
            {piechartData?.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry?.color} />
            ))}
          </Pie>
        </PieChart>
        <Box sx={{ mt: 1 }}>
          {piechartData?.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                width: 250,
                display: "flex",
                justifyContent: "space-between",
                fontSize: "13px",
                color: "#333",
                mb: 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: item.color,
                  }}
                />
                <Typography>{item?.name}</Typography>
              </Box>
              <Typography>{item?.value}%</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Box sx={{ p: 2, mt: 3 }}>
        {/* Charts */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          {renderDonut("Total Inactive Zip Code", pieChart)}
          {renderDonut("Total Active Zip Code", pieChart2)}
        </Box>

        {/* Table */}
        <Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Zip Code</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Questionnaire</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zipLoading ? (
                  // Loading skeleton rows
                  [...Array(itemsPerPage)].map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={60} height={32} /></TableCell>
                    </TableRow>
                  ))
                ) : (
                  // Actual data rows
                  waitlist.map((item, index) => (
                    <TableRow key={item._id || index}>
                      <TableCell>
                        {new Date(item?.createdAt).toLocaleString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </TableCell>
                      <TableCell>{item.zipCode}</TableCell>
                      <TableCell>{item.country?.name}</TableCell>
                      <TableCell>5/5</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDrawerOpen(item)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 1,
              fontSize: "12px",
              color: "#555",
            }}
          >
            <Typography>
              Show result: {waitlist.length} out of {paginationInfo.totalItems || 0}
            </Typography>
            <Pagination 
              count={paginationInfo.totalPages || 1} 
              page={currentPage} 
              onChange={handlePageChange}
              size="small" 
              disabled={zipLoading}
            />
          </Box>
        </Box>
      </Box>

      <ZipCodeDetails
        open={drawerOpen}
        data={selectedItem}
        onClose={handleDrawerClose}
      />
    </>
  );
};

export default ZipTracker;
